/**
 * DocumentSignSettingsDialog — edits the per-user Document Sign defaults
 * (display name, email, envelope title, recipient message, and the
 * include-filename toggle).
 *
 * The dialog is form-local; it commits to `useArcheSettings` via `onSave`
 * only when the user clicks Save.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { DocumentSignSettings } from '../settings';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface DocumentSignSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: DocumentSignSettings;
  onSave: (next: DocumentSignSettings) => void;
}

export function DocumentSignSettingsDialog({
  open,
  onOpenChange,
  settings,
  onSave,
}: DocumentSignSettingsDialogProps) {
  const [displayName, setDisplayName] = useState(settings.defaultDisplayName);
  const [email, setEmail] = useState(settings.defaultEmail);
  const [title, setTitle] = useState(settings.defaultTitle);
  const [message, setMessage] = useState(settings.defaultMessage);
  const [includeFilename, setIncludeFilename] = useState(settings.includeFilename);
  const [error, setError] = useState<string | null>(null);

  // Reset the form from the stored settings each time the dialog opens.
  useEffect(() => {
    if (open) {
      setDisplayName(settings.defaultDisplayName);
      setEmail(settings.defaultEmail);
      setTitle(settings.defaultTitle);
      setMessage(settings.defaultMessage);
      setIncludeFilename(settings.includeFilename);
      setError(null);
    }
  }, [open, settings]);

  function handleSave() {
    const trimmedEmail = email.trim();
    if (trimmedEmail && !EMAIL_RE.test(trimmedEmail)) {
      setError('Enter a valid default email, or leave it blank.');
      return;
    }
    onSave({
      defaultDisplayName: displayName.trim(),
      defaultEmail: trimmedEmail.toLowerCase(),
      defaultTitle: title.trim(),
      defaultMessage: message.trim(),
      includeFilename,
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Document Sign settings</DialogTitle>
          <DialogDescription>
            Defaults for creating envelopes and adding yourself as a recipient. Stored on this
            device.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ds-default-name">Default Display Name</Label>
            <Input
              id="ds-default-name"
              value={displayName}
              maxLength={300}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Jane Smith"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ds-default-email">Default Email</Label>
            <Input
              id="ds-default-email"
              type="email"
              value={email}
              maxLength={500}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. jane@example.com"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ds-default-title">Default Envelope Title</Label>
            <Input
              id="ds-default-title"
              value={title}
              maxLength={300}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Complete with Fias eSign:"
            />
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="ds-include-filename"
                checked={includeFilename}
                onCheckedChange={(checked) => setIncludeFilename(checked)}
              />
              <Label htmlFor="ds-include-filename" className="text-sm font-normal">
                Include filename
              </Label>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ds-default-message">Default Message</Label>
            <Textarea
              id="ds-default-message"
              value={message}
              maxLength={5000}
              rows={4}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Please review and sign at your earliest convenience."
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
